/**
 * Robust browser-download helper.
 *
 * Reported M6 gate defect:
 *   Portfolio "Direction Snapshot PDF" and Settings "Export" both fetched
 *   the file successfully, but the download either never appeared or
 *   the UI showed "Download failed" while the backend returned 200. Root
 *   causes: (1) `URL.revokeObjectURL` firing synchronously after
 *   `a.click()` can cancel the in-flight download in some browsers (esp.
 *   Safari, WebKit); (2) toast state was mis-timed against the browser's
 *   actual download queue.
 *
 * This helper:
 *   - Uses fetch() with the current auth token so a plain <a href> can't
 *     hit an unauthenticated PDF/export endpoint.
 *   - Streams the response into a Blob with the correct MIME type.
 *   - Creates an anchor inside a hidden container, dispatches a real
 *     click event, then revokes the object URL after a 60s window so the
 *     browser has time to write the file to disk.
 *   - Rejects loudly on real failure so the caller shows an honest error
 *     state (the FAIL 3 rule: never claim success if the file did not
 *     actually reach the user).
 */
import { API_BASE, getToken } from "@/lib/api";

const DEFAULT_MIME = "application/octet-stream";
const REVOKE_DELAY_MS = 60_000;


/**
 * Download an authenticated GET endpoint as a file. Returns a Promise
 * that resolves with the byte length on success and rejects with an
 * Error carrying a plain-language message on failure.
 *
 * @param {string} path  API path starting with `/`
 * @param {string} filename Filename hint for the browser
 * @param {{ mime?: string, transform?: (Blob) => Promise<Blob>|Blob }} opts
 */
export async function authenticatedDownload(path, filename, opts = {}) {
  const token = getToken();
  if (!token) {
    throw new Error("You need to be signed in to download this file.");
  }
  const url = `${API_BASE}${path}`;
  let response;
  try {
    response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (netErr) {
    throw new Error("The network dropped before the file arrived. " +
                     "Please check your connection and try again.");
  }
  if (!response.ok) {
    let msg = null;
    try {
      const body = await response.clone().json();
      msg = body?.detail?.message || body?.detail || body?.message;
      if (Array.isArray(msg)) msg = msg.map((m) => m.msg || m).join("; ");
    } catch (_) {
      // response isn't JSON
    }
    throw new Error(msg ||
      `The server refused the download (${response.status}). ` +
      "Nothing was saved.");
  }
  let blob = await response.blob();
  if (opts.transform) {
    try { blob = await opts.transform(blob); }
    catch (e) { throw new Error("Could not prepare the file: " + (e?.message || e)); }
  }
  if (opts.mime) blob = blob.slice(0, blob.size, opts.mime);
  else if (!blob.type) blob = blob.slice(0, blob.size, DEFAULT_MIME);

  if (!blob.size) {
    throw new Error("The server returned an empty file. " +
                     "Please try again in a moment.");
  }

  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  // Use a real user-generated click event so Safari + Firefox honor it.
  a.dispatchEvent(new MouseEvent("click", {
    bubbles: true, cancelable: true, view: window,
  }));
  a.remove();
  // Give the browser plenty of time to actually queue the download
  // before we release the blob. 60s is well past any real completion.
  window.setTimeout(() => {
    try { window.URL.revokeObjectURL(objectUrl); } catch (_) {}
  }, REVOKE_DELAY_MS);
  return blob.size;
}


/**
 * Download JSON returned from an API endpoint as a file. Rejects on
 * failure with an honest message (the caller MUST NOT display a
 * success state without awaiting this Promise).
 */
export async function jsonDownload(path, filename) {
  return authenticatedDownload(path, filename, {
    mime: "application/json",
    transform: async (blob) => {
      const raw = await blob.text();
      // Pretty-print for a human-readable export file
      const obj = JSON.parse(raw);
      const pretty = JSON.stringify(obj, null, 2);
      return new Blob([pretty], { type: "application/json" });
    },
  });
}
